import $ from 'jquery';

import CheckoutSectionView from 'chairisher/view/checkout/checkoutsection';
import CountryView from 'chairisher/view/account/countries';
import CreditCardComponent from 'chairisher/component/creditcard';
import Disclaimers from 'chairisher/view/product/disclaimers';
import FormFieldComponent from 'chairisher/component/form/field';
import FormUtils from 'chairisher/util/form';
import KeyUtils from 'chairisher/util/key';

import { getRequiredFieldErrorString } from 'chairisher/context/error';
import { isAddressAutocompleteEnabled } from 'chairisher/context/google';
import {
    getBraintreeClientToken,
    getBraintreePaymentMethodToken,
    getPaymentProcessorCodes,
    getPaymentRequestWalletNames,
    removeBraintreePaymentMethodToken,
} from 'chairisher/context/payment';
import { isOffer, getPurchaseObject } from 'chairisher/context/purchase';
import { initializeBraintree } from 'chairisher/thirdparty/braintreehelper';
import {
    trackDiscountSubmitError,
    trackDiscountSubmitSuccess,
    trackGiftCardApplied,
    trackPaymentMethodSelection,
    trackPaymentSubmitError,
    trackPaymentSubmitSuccess,
} from 'chairisher/analytics/checkout';
import { handleAsyncError } from 'chairisher/util/logging';
import { getProcessingFeeLineItem } from 'chairisher/view/checkout/receipt';

const billingAddressFieldNames = [
    'city',
    'country_code',
    'name',
    'phone_number',
    'postal_code',
    'state',
    'street_address_line_1',
    'street_address_line_2',
];

/**
 * View used to manage payment options in the checkout flow.
 */
class CheckoutPaymentView extends CheckoutSectionView {
    constructor(selector) {
        super(selector);

        /**
         * Object returned from Braintree representing the device the payment is being made on
         *
         * @type {Object}
         */
        this.braintreeDeviceData = null;

        /**
         * Secure, one-time-use reference to payment information returned by Braintree
         *
         * @type {string|null}
         */
        this.braintreeNonce = null;

        /**
         * Flag indicating if there are Stripe errors after attempting to create a token
         */
        this.hasStripeErrors = false;

        /**
         * Stripe Card element that uses a Stripe IFRAME to send data to Stripe
         *
         * @see https://stripe.com/docs/js/element
         * @type {StripeElement}
         */
        this.stripeCardElement = null;

        /**
         * Data containing a token used to create a Stripe card on the server
         *
         * @type {Object|null}
         */
        this.stripeData = null;
    }

    initialize() {
        super.initialize();

        if (this.hasBraintreePaymentOption()) {
            initializeBraintree({
                braintreeToken: getBraintreeClientToken(),
                setDeviceData: (braintreeDeviceData) => {
                    // Don't overwrite the device data if it's already set.
                    this.braintreeDeviceData = this.braintreeDeviceData || braintreeDeviceData;
                },
                onFail: () => this.getBraintreeWrapper().remove(),
                checkoutCallback: (paypal, checkout) => this.paypalButtonCallback(paypal, checkout),
            });
        }

        // Run logic for show/hide UI elements on load.
        this.toggleBraintreeWidgetVisibility(this.isBraintreePaymentMethod());
    }

    /**
     * @inheritDoc
     */
    beforeUpdate() {
        const deferred = $.Deferred();
        if (this.isStripeElementsPaymentMethod() && !this.getStripeCardToken() && !this.getSelectedCreditCard()) {
            this.createStripeCardToken().then(() => deferred.resolve(), handleAsyncError);
        } else {
            deferred.resolve();
        }
        return deferred;
    }

    /**
     * Sets the braintree deviceData and nonce.
     *
     * @param {object}
     */
    setBraintreeData({ deviceData, nonce }) {
        this.braintreeNonce = nonce;
        this.braintreeDeviceData = deviceData;
    }

    /**
     * @param {boolean} shouldShow Whether to make the discount link visible.
     */
    toggleDiscountLinkVisibility(shouldShow) {
        this.$fieldsEl.find('.js-discount-toggle').toggleClass('hidden', !shouldShow);
    }

    /**
     * @param {boolean} shouldShow Whether to make the processing fee disclaimer visible.
     */
    toggleProcessingFeeDisclaimer(shouldShow) {
        this.$fieldsEl.find('.js-processing-fee-disclaimer').toggleClass('hidden', !shouldShow);
    }

    /**
     * @param {boolean} shouldShow Whether to make the Stripe credit card widget visible.
     */
    toggleStripeWidgetVisibility(shouldShow) {
        if (shouldShow) {
            this.toggleBraintreeWidgetVisibility(false);
        }
        this.$fieldsEl.find('#js-stripe-payment-method').toggleClass('hidden', !shouldShow);
    }

    /**
     * @param {boolean} shouldShow Whether to make the Braintree button visible.
     */
    toggleBraintreeWidgetVisibility(shouldShow) {
        const hasBrainTreeData = !!this.braintreeNonce || !!getBraintreePaymentMethodToken();

        if (shouldShow) {
            this.toggleStripeWidgetVisibility(false);
        }

        const shouldHideAccount = !shouldShow || !hasBrainTreeData;
        const shouldHidePaypalButton = !shouldShow || hasBrainTreeData;
        const shouldHideNext = !shouldHidePaypalButton;
        this.$fieldsEl.find('.js-next').toggleClass('hidden', shouldHideNext);
        this.$fieldsEl.find('.js-paypal-account-wrapper').toggleClass('hidden', shouldHideAccount);

        // Help text explains how to use the PayPal button. They should both be shown/hidden under same conditions.
        this.$fieldsEl.find('.js-paypal-help-text').toggleClass('hidden', shouldHidePaypalButton);
        this.getPayPalButton().toggleClass('hidden', shouldHidePaypalButton);
    }

    /**
     * @param {boolean} isPaymentMethod whether the payment request button (Apple Pay/Google Pay) should be selected.
     */
    togglePaymentRequestMethod(isPaymentMethod) {
        if (isPaymentMethod) {
            this.checkoutCallbacks.setPaymentRequestButton();
            this.toggleBraintreePaymentMethod(false);
            this.toggleBraintreeWidgetVisibility(false);
            this.toggleStripePaymentMethod(false);
            this.toggleStripeWidgetVisibility(false);
        }
        this.getPaymentRequestOption().prop('checked', isPaymentMethod);
    }

    /**
     * Toggles whether Stripe is the selected payment method
     *
     * @param {boolean} isPaymentMethod
     */
    toggleStripePaymentMethod(isPaymentMethod) {
        if (isPaymentMethod) {
            this.toggleStripeWidgetVisibility(true);
            this.togglePaymentRequestMethod(false);
            this.toggleBraintreePaymentMethod(false);
        }
        this.getStripePaymentOption().prop('checked', isPaymentMethod);
    }

    /**
     * Toggles whether Braintree is the selected payment method
     *
     * @param {boolean} isPaymentMethod
     */
    toggleBraintreePaymentMethod(isPaymentMethod) {
        if (isPaymentMethod) {
            this.toggleBraintreeWidgetVisibility(true);
            this.togglePaymentRequestMethod(false);
            this.toggleStripePaymentMethod(false);
        }

        this.getBraintreePaymentOption().prop('checked', isPaymentMethod);
    }

    /**
     * @returns {jQuery} Button for redeeming a discount code.
     */
    getRedeemButton() {
        return this.$fieldsEl.find('.js-btn-redeem');
    }

    /**
     * @returns {jQuery} Container that wraps discount/gift card input
     */
    getDiscountContainer() {
        return this.$fieldsEl.find('.js-discount-container');
    }

    /**
     * @returns {jQuery} The PayPal buttons containers
     */
    getPayPalButton() {
        return this.$fieldsEl.find('.js-paypal-button');
    }

    /**
     * @returns {jQuery} The payment request option element
     */
    getPaymentRequestOption() {
        return this.$fieldsEl.find('#js-payment-request-radio-button');
    }

    /**
     * Wraps the payment request button, e.g. Apple Pay or Google Pay.
     *
     * @returns {jQuery} This should be hidden by default, and unhidden only if the device is supported.
     */
    getPaymentRequestWrapper() {
        return this.$fieldsEl.find('.js-payment-request-wrapper');
    }

    /**
     * @returns {jQuery} Collapsable container that wraps the billing address form
     */
    getBillingAddressWrapper() {
        return this.$fieldsEl.find('.js-billing-address-wrapper');
    }

    /**
     * @returns {jQuery} Collapsable container that wraps the Stripe credit card input
     */
    getCreditCardWrapper() {
        return this.$fieldsEl.find('.js-credit-card-input');
    }

    /**
     * @returns {jQuery} The Stripe payment option element
     */
    getStripePaymentOption() {
        return this.$fieldsEl.find('#js-credit-card-radio-button');
    }

    getBraintreeWrapper() {
        return this.$fieldsEl.find('.js-payment-method-braintree-wrapper');
    }

    getBraintreeDeleteButton() {
        return this.$fieldsEl.find('.js-braintree-delete');
    }

    /**
     * @returns {jQuery} The BrainTree payment option element
     */
    getBraintreePaymentOption() {
        return this.$fieldsEl.find('#js-paypal-radio-button');
    }

    /**
     * @returns {jQuery} The payment method radio buttons.
     */
    getPaymentMethodRadioButtons() {
        return this.$fieldsEl.find('input[name="payment-method-radio-button"]');
    }

    /**
     * @returns {boolean} True indicates that Braintree is a payment option that can be selected
     */
    hasBraintreePaymentOption() {
        return this.getBraintreePaymentOption().length > 0;
    }

    /**
     * @inheritDoc
     */
    isValid() {
        let isValid = super.isValid();
        if (isValid && this.isPaymentRequired()) {
            if (this.getComplexErrors()) {
                isValid = false;
            } else if (this.isStripeElementsPaymentMethod()) {
                isValid = !!this.getStripeValue() && !this.hasStripeErrors;
            } else if (this.isPaymentRequestMethod()) {
                isValid = true;
            } else {
                isValid = !!this.getBraintreeValue();
            }
        }

        return isValid;
    }

    /**
     * @inheritDoc
     */
    renderForm() {
        super.renderForm();
        const data = getPurchaseObject();
        const promotionCode = data.purchase_details.promotion_code;
        const promotionCodeDisplay = promotionCode && promotionCode.display;
        const $discountEl = this.$fieldsEl.find('.js-discount');
        $discountEl.find('.js-confirmation-container').toggleClass('hidden', !promotionCodeDisplay);
        $discountEl.find('.js-confirmation').text(promotionCodeDisplay);

        const $cardSelection = this.$fieldsEl.find('.js-credit-card-selection');

        if (data.payment_method.is_payment_required?.value) {
            if (
                this.isStripeElementsPaymentMethod() &&
                !(data.payment_method?.stripe_token?.value || data.payment_method.stripe_payment_card_id?.value)
            ) {
                this.stripeData = null;
                this.mountStripeElements();
            }
        } else {
            $cardSelection.collapse('hide');
            this.resetStripeData();
            this.deselectSavedCreditCards();
        }

        if (
            !this.isCreditCardFormOpen() &&
            billingAddressFieldNames.some((key) => !this.fieldNameToComponentMap[key].isValid())
        ) {
            // If the user closed credit card form while the billing address is an invalid state clear it.
            // This prevents hidden errors which would block the user from progressing.
            this.clearBillingAddress();
        }
    }

    /**
     * @inheritDoc
     */
    renderErrors() {
        super.renderErrors();

        const complexErrors = this.getComplexErrors();
        if (complexErrors) {
            this.$fieldsEl
                .find('.js-payment-method-errors')
                .html(FormFieldComponent.marshalMessageList(complexErrors, 'error-item'));
        } else {
            this.$fieldsEl.find('.js-payment-method-errors').children().remove();
        }
    }

    /**
     * Whether the user indicated that they would like to save their credit card.
     *
     * @returns {boolean}
     */
    shouldSaveCard() {
        return this.$fieldsEl.find('[name="should_save_card"]').is(':checked');
    }

    /**
     * Toggles the visibility of the credit card input field
     *
     * @param {boolean} shouldDisplay True indicates the field should be displayed
     */
    toggleCreditCardInput(shouldDisplay) {
        this.getCreditCardWrapper().collapse(shouldDisplay ? 'show' : 'hide');
        this.$fieldsEl.find('.js-expand-credit-card-input').toggleClass('hidden', shouldDisplay);
        if (shouldDisplay) {
            this.stripeCardElement.focus();
        }
    }

    /**
     * @inheritDoc
     */
    getDataFromContext() {
        const purchaseData = getPurchaseObject();
        return {
            ...purchaseData.billto_address,
            ...purchaseData.payment_method,
            ...purchaseData.purchase_details,
        };
    }

    /**
     * @inheritdoc
     */
    bind() {
        const $savedCards = this.getCreditCardElements();
        let wasFirstSelectableCardFound = false;
        $.each($savedCards, (i) => {
            const $cardEl = $savedCards.eq(i);
            const canSelect = !$cardEl.hasClass('disabled') && !$cardEl.hasClass('expired');
            const card = new CreditCardComponent({
                $el: $cardEl,
                cardId: $cardEl.data('card-id'),
                canSelect,
            });
            card.initialize();

            if (canSelect && !wasFirstSelectableCardFound) {
                card.select();
                wasFirstSelectableCardFound = true;
            }
        });

        this.$fieldsEl.on('creditcard:selected', '.js-card', (e) => {
            const $cards = this.$fieldsEl.find('.js-card').not($(e.currentTarget));
            $.each($cards, (i) => {
                $cards.eq(i).data('creditcard').deselect();
            });

            this.resetStripeData();
            this.renderStripeErrors();
            this.toggleCreditCardInput(false);
            this.toggleStripePaymentMethod(true);
        });

        //
        // Add a new card
        //

        this.$fieldsEl.on('click', '.js-expand-credit-card-input', (e) => {
            e.preventDefault();
            this.toggleCreditCardInput(true);
            $.each(this.getCreditCardComponents(), (_, card) => {
                card.deselect();
            });
        });

        //
        // Billing address
        //

        const $billingAddressWrapper = this.getBillingAddressWrapper();
        this.$fieldsEl.find('[name="billing_address_toggle"]').on('change', (e) => {
            const $toggle = $(e.target);
            const isChecked = $toggle.is(':checked');

            if (isChecked) {
                this.clearBillingAddress();
            }

            $billingAddressWrapper.collapse(isChecked ? 'hide' : 'show');
        });

        //
        // Google Autocomplete Address
        //
        if (isAddressAutocompleteEnabled()) {
            import('chairisher/component/autocomplete/google/address').then(
                ({ default: GoogleAutocompleteAddress }) => {
                    const googleAutocompleteAddress = new GoogleAutocompleteAddress({
                        $addressFields: $billingAddressWrapper.find('.js-address-fields'),
                    });
                    googleAutocompleteAddress.bind();
                },
                handleAsyncError,
            );
        }

        const countryView = new CountryView({
            $countryEl: $billingAddressWrapper.find('.js-address-country'),
            shouldLimitToShippableStates: true,
        });
        countryView.bind();
        countryView.updateForCurrentCountry();

        //
        // Discounts / Gift Cards
        //
        this.$summaryEl.find('.js-discount-card-trigger').on('click', (e) => {
            e.preventDefault();
            this.showDiscountInput();
            this.checkoutCallbacks.editStep(this.getStepNumber());
        });

        this.$summaryEl.find('.js-summary-card').on('click', () => {
            this.checkoutCallbacks.editStep(this.getStepNumber());
        });

        this.$fieldsEl.find('.js-discount-toggle').on('click', (e) => {
            e.preventDefault();
            this.showDiscountInput();
        });

        this.getDiscountContainer().on('input keydown', (e) => {
            if (KeyUtils.isEnter(e.which)) {
                e.preventDefault();
                this.submitDiscountCode();
            }
        });

        this.getRedeemButton().on('click', (e) => {
            e.preventDefault();
            this.submitDiscountCode();
        });

        //
        // Payment Selection Radio Button
        //
        this.getPaymentMethodRadioButtons().on('change', ({ currentTarget: { value } }) => {
            let shouldTrackProcessingFee = true;
            this.toggleDiscountLinkVisibility(true);
            this.toggleProcessingFeeDisclaimer(true);
            const paymentProcessors = getPaymentProcessorCodes();
            const walletNames = getPaymentRequestWalletNames();
            switch (value) {
                case walletNames.APPLE_PAY:
                case walletNames.GOOGLE_PAY:
                    this.selectPaymentRequestButton();
                    break;
                case paymentProcessors.BRAINTREE:
                    this.selectBraintreeRadioButton();
                    shouldTrackProcessingFee = false;
                    break;
                case paymentProcessors.STRIPE:
                    this.selectStripeRadioButton();
                    break;
                default:
                    break;
            }
            // Re-fetch the purchase object since it can change the receipt total.
            this.checkoutCallbacks.updatePurchaseObject();
            trackPaymentMethodSelection(value, false, shouldTrackProcessingFee);
        });

        this.getBraintreeDeleteButton().on('click', (e) => {
            // Remove client side references to BT data.
            removeBraintreePaymentMethodToken();
            this.braintreeNonce = null;
            this.toggleBraintreePaymentMethod(true);

            // Optionally make a backend call to delete the token from database.
            // Success/failure doesn't really matter.
            const paymentMethodId = $(e.currentTarget).data('payment-method-id');
            if (paymentMethodId) {
                $.ajax({
                    data: { payment_method_token: paymentMethodId },
                    dataType: 'json',
                    method: 'POST',
                    url: $(e.currentTarget).data('action'),
                });
            }
        });
    }

    submitDiscountCode() {
        const $discountContainer = this.getDiscountContainer();
        const discountValue = this.getDiscountContainer().find('input').val();
        FormUtils.setFormGroupErrorMessage(
            $discountContainer.find('.form-group'),
            discountValue ? null : getRequiredFieldErrorString(),
        );

        if (discountValue) {
            this.getRedeemButton().prop('disabled', true);
            this.checkoutCallbacks.updateEditableView(true).always(() => {
                this.getRedeemButton().prop('disabled', false);
                this.trackDiscountEvent();
            });
        }
    }

    /**
     * Shows and focuses the input element for entering a discount code.
     */
    showDiscountInput() {
        this.toggleDiscountLinkVisibility(false);
        const $discountContainer = this.getDiscountContainer();
        $discountContainer.removeClass('hidden');
        // Wait a bit before setting focus, otherwise browser might ignore.
        window.setTimeout(() => $discountContainer.find('input').focus(), 100);
    }

    /**
     * Hides alternative payment fields and expands the payment request button, e.g. Google Pay / Apple Pay.
     */
    selectPaymentRequestButton() {
        this.togglePaymentRequestMethod(true);
    }

    /**
     * Hides alternative payment fields and expands Stripe's field when selected
     */
    selectStripeRadioButton() {
        this.checkoutCallbacks.setDefaultSubmitButtonText();
        this.toggleStripePaymentMethod(true);
    }

    /**
     * Hides alternative payment fields and expands Braintree's field when selected
     */
    selectBraintreeRadioButton() {
        this.checkoutCallbacks.setDefaultSubmitButtonText();
        this.toggleBraintreePaymentMethod(true);
        this.toggleProcessingFeeDisclaimer(false);
    }

    /**
     * Clears the billing address fields
     */
    clearBillingAddress() {
        billingAddressFieldNames.forEach((key) => {
            this.fieldNameToComponentMap[key].setData('');
        });
        this.$fieldsEl.find('.js-address-fields [name]').val('');
    }

    /**
     * Creates a new Stripe credit card token
     *
     * @returns {Promise} Native JS Promise that will execute success and failure callbacks when the request is done
     */
    createStripeCardToken() {
        // Provide address data for AVS (address verification service) checks.
        const purchaseData = getPurchaseObject();
        const addressTypeKey = this.getBillingAddressValue() ? 'billto_address' : 'shipto_address';
        const getAddressPart = (addressPartKey) => purchaseData[addressTypeKey]?.[addressPartKey]?.value;
        const tokenData = {
            address_zip: getAddressPart('postal_code'),
            address_line1: getAddressPart('street_address_line_1'),
            address_country: getAddressPart('country_code'),
        };

        return this.checkoutCallbacks
            .getStripe()
            .createToken(this.stripeCardElement, tokenData)
            .then((stripeData) => {
                this.resetStripeData();
                this.hasStripeErrors = !!stripeData.error;
                if (this.hasStripeErrors) {
                    this.renderStripeErrors(stripeData);
                } else {
                    this.stripeData = stripeData;
                }
            }, handleAsyncError);
    }

    /**
     * Deselects saved credit cards
     */
    deselectSavedCreditCards() {
        Object.values(this.getCreditCardComponents()).forEach((component) => {
            component.deselect();
        });
    }

    /**
     * @returns {string} Stripe token used to create a new credit card on the server
     */
    getStripeCardToken() {
        return this.stripeData?.token?.id;
    }

    /**
     * @returns {Object|undefined} Object containing billing address info, if any
     */
    getBillingAddressValue() {
        let hasBillingAddress = false;

        const billingAddress = {};
        Object.entries(super.getValue() || {}).forEach(([key, val]) => {
            if (billingAddressFieldNames.includes(key)) {
                if (key !== 'country_code') {
                    hasBillingAddress = true;
                }
                billingAddress[key] = val;
            }
        });
        return hasBillingAddress ? billingAddress : undefined;
    }

    /**
     * @returns {string} The currently selected payment method value
     */
    getPaymentMethodValue() {
        return this.getPaymentMethodRadioButtons().filter(':checked').val();
    }

    /**
     * @returns {Object|undefined} The value of the selected credit card processor
     */
    getPaymentProcessorValue() {
        let value = this.getPaymentMethodValue();
        if (Object.values(getPaymentRequestWalletNames()).includes(value)) {
            // Stripe processes payment requests.
            value = getPaymentProcessorCodes().STRIPE;
        }
        return value ? { value } : undefined;
    }

    /**
     * @returns {Object} The value of the promotion code that has been entered
     */
    getPromotionCodeValue() {
        return {
            value: this.$fieldsEl.find('[name="promotion_code"]').val() || '',
        };
    }

    /**
     * @returns {Object|undefined} The Braintree data to submit
     */
    getBraintreeValue() {
        let braintreeValue;
        if (this.isBraintreePaymentMethod()) {
            braintreeValue = {};

            if (this.braintreeDeviceData) {
                // Set on page load after braintree library initializes.
                braintreeValue.braintree_device_data = { value: this.braintreeDeviceData };
            }
            if (this.braintreeNonce) {
                // Set when the user is redirected back to Chairish
                // after inputting payment details in PayPal's hosted form.
                braintreeValue.braintree_nonce = { value: this.braintreeNonce };
            }
            const token = getBraintreePaymentMethodToken();
            if (token) {
                // Set on page load if the user has already saved their braintree account.
                braintreeValue.braintree_payment_method_token = { value: token };
            }
        }
        return braintreeValue;
    }

    /**
     * @returns {Object|undefined} The Stripe data to submit
     */
    getStripeValue() {
        let stripeValue;
        const isElements = this.isStripeElementsPaymentMethod();
        const isPaymentRequest = this.isPaymentRequestMethod();
        if (isElements || isPaymentRequest) {
            const stripeCardToken = this.getStripeCardToken();

            if (stripeCardToken) {
                stripeValue = {
                    stripe_token: { value: stripeCardToken },
                    should_save_card: { value: isElements && this.shouldSaveCard() },
                };
            } else if (isPaymentRequest && this.checkoutCallbacks.getCurrentStepNumber() <= this.getStepNumber()) {
                // Tell the server to validate this section without a token set, it will be collected on final step.
                stripeValue = { is_token_deferred: { value: true } };
            } else if (isElements) {
                const selectedCard = this.getSelectedCreditCard();
                if (selectedCard) {
                    stripeValue = { stripe_payment_card_id: { value: selectedCard.getCardId() } };
                }
            }
        }

        return stripeValue;
    }

    /**
     * @inheritDoc
     */
    getValue() {
        const value = {
            billto_address: this.getBillingAddressValue(),
            payment_method: this.getBraintreeValue() || this.getStripeValue() || {},
            purchase_details: { promotion_code: this.getPromotionCodeValue() },
        };

        if (this.isPaymentRequired()) {
            // e.g. for gift cards, we shouldn't set a payment processor
            value.payment_method.payment_processor = this.getPaymentProcessorValue();
        }

        return value;
    }

    /**
     * @returns {Array.<CreditCard>} Collection of saved credit card JS components
     * @see CreditCard
     */
    getCreditCardComponents() {
        const $creditCards = this.getCreditCardElements();
        const components = [];
        $.each($creditCards, (i) => {
            components.push($creditCards.eq(i).data('creditcard'));
        });

        return components;
    }

    /**
     * @returns {CreditCard|undefined}
     */
    getSelectedCreditCard() {
        return this.getCreditCardComponents().find((creditCard) => creditCard.isSelected);
    }

    /**
     * @returns {Array.<jQuery>} Collection of saved credit card ui elements
     */
    getCreditCardElements() {
        return this.$fieldsEl.find('.js-card');
    }

    /**
     * Renders the PayPal account.
     *
     * @param email {string}
     */
    renderPaypalAccount(email) {
        this.$fieldsEl.find('.js-paypal-account').text(email);
        $('.review-and-confirm .is-opted-in').removeClass('hidden');
        $('.shipping-address-fields .is-opted-in').addClass('hidden');
    }

    /**
     * Callback for creating and handling the PayPal button.
     *
     * @param {Object} paypal
     * @param {Object} checkout
     */
    paypalButtonCallback(paypal, checkout) {
        return paypal
            .Buttons({
                fundingSource: paypal.FUNDING.PAYPAL,
                createBillingAgreement: () => checkout.createPayment({ flow: 'vault' }),
                onApprove: (data) =>
                    checkout.tokenizePayment(data).then((payload) => {
                        this.braintreeNonce = payload.nonce;
                        this.toggleBraintreePaymentMethod(true);
                        this.deselectSavedCreditCards();
                        this.clearBillingAddress();
                        this.checkoutCallbacks.updateEditableView();
                        this.renderPaypalAccount(payload.details.email);
                        this.checkoutCallbacks.editStep(this.getStepNumber() + 1);
                        trackPaymentSubmitSuccess(getPaymentProcessorCodes().BRAINTREE, false);
                    }, handleAsyncError),
                style: {
                    color: 'blue',
                    height: 34,
                    label: isOffer() ? 'paypal' : 'pay',
                    shape: 'rect',
                    size: 'responsive',
                },
            })
            .render('.js-paypal-button');
    }

    /**
     * @returns {Array.<Object>|null} Array of errors on non-simple fields or null when there are no errors.
     */
    getComplexErrors() {
        let complexErrors = null;
        const data = getPurchaseObject();
        const paymentProcessor = (data.payment_method && data.payment_method.payment_processor) || {};
        const stripeToken = (data.payment_method && data.payment_method.stripe_token) || {};
        const stripeCardId = (data.payment_method && data.payment_method.stripe_payment_card_id) || {};
        if (this.isBraintreePaymentMethod() && !this.braintreeNonce && !getBraintreePaymentMethodToken()) {
            // Ignore paymentProcessor.errors in this case since the server doesn't send a user friendly message.
            // TODO (CHAIR-16034): Remove this block/error message after implementing new PayPal design.
            const display =
                'To pay with PayPal, please click the blue PayPal button before clicking "Use This Payment Method".';
            complexErrors = [{ display }];
        } else if (paymentProcessor.errors) {
            Disclaimers.render(data.products);
            complexErrors = paymentProcessor.errors;
        } else if (stripeToken.errors) {
            complexErrors = stripeToken.errors;
        } else if (stripeCardId.errors) {
            complexErrors = stripeCardId.errors;
        } else if (this.isCreditCardFormOpen() && this.isBillingAddressFormOpen() && !this.getBillingAddressValue()) {
            // If the billing address form is visible without value
            complexErrors = [{ display: 'Please enter a billing address' }];
        }
        if (complexErrors && complexErrors.length === 0) {
            complexErrors = null;
        }
        return complexErrors;
    }

    /**
     * Initializes the Stripe IFRAME that accepts the credit card info
     */
    initializeStripeElements() {
        const elements = this.checkoutCallbacks.getStripe().elements();
        this.stripeCardElement = elements.create('card', {
            hidePostalCode: true,
            style: {
                // these styles get passed into the iframe
                base: {
                    fontSize: '14px',
                    color: '#000000',
                },
            },
        });

        this.stripeCardElement.on('change', (stripeEvent) => {
            this.renderStripeErrors(stripeEvent);
            if (!stripeEvent.complete && this.stripeData) {
                this.resetStripeData();
            }
        });
    }

    /**
     * @returns {boolean} True indicates the selected payment method is Braintree
     */
    isBraintreePaymentMethod() {
        return this.getBraintreePaymentOption().is(':checked');
    }

    /**
     * @returns {boolean} True indicates a form of payment is required; false indicates gift card balance covers payment
     */
    isPaymentRequired() {
        // Make sure to only return false if the property is explicitly set - parens are required.
        return !(getPurchaseObject().payment_method.is_payment_required?.value === false);
    }

    /**
     * @returns {boolean} True indicates the payment request method is selected e.g. Apple Pay or Google Pay.
     */
    isPaymentRequestMethod() {
        return this.getPaymentRequestOption().is(':checked');
    }

    /**
     * @returns {boolean} True indicates the selected payment method is Stripe
     */
    isStripeElementsPaymentMethod() {
        return this.getStripePaymentOption().is(':checked');
    }

    /**
     * @returns {boolean} True indicates the billing address form is open
     */
    isBillingAddressFormOpen() {
        return this.getBillingAddressWrapper().hasClass('in');
    }

    /**
     * @returns {boolean} True indicates the credit card form is open
     */
    isCreditCardFormOpen() {
        return this.getCreditCardWrapper().hasClass('in');
    }

    mountStripeElements() {
        if (this.stripeCardElement) {
            this.stripeCardElement.unmount();
            this.stripeCardElement.mount('#js-stripe-card-element');
        }
    }

    /**
     * @inheritDoc
     */
    edit() {
        super.edit();
        if (getProcessingFeeLineItem()) {
            this.checkoutCallbacks.updatePurchaseObject();
        }
    }

    /**
     * @inheritDoc
     */
    populateFieldNameToComponentMap() {
        const data = this.getDataFromContext();
        const allFieldNames = [...billingAddressFieldNames, 'promotion_code'];
        allFieldNames.forEach((key) => {
            const component = new FormFieldComponent({
                $el: this.$el.find(`[name="${key}"]`),
                name: key,
                data: data[key] || { value: null },
            });
            component.render();
            this.fieldNameToComponentMap[key] = component;
        });
    }

    /**
     * @inheritDoc
     */
    renderSummary() {
        let $card = '';
        if (this.isStripeElementsPaymentMethod()) {
            if (this.stripeData) {
                const data = this.stripeData.token.card;
                $card = CreditCardComponent.marshalCreditCard({
                    brand: data.brand,
                    card_id: data.id,
                    last4: data.last4,
                    exp_month: data.exp_month,
                    exp_year: data.exp_year,
                });
            } else {
                const selectedCard = this.getSelectedCreditCard();
                if (selectedCard) {
                    $card = selectedCard.getEl().clone();
                    $card.removeClass('selected');
                }
            }
        } else if (this.isBraintreePaymentMethod()) {
            $card = this.getBraintreeWrapper().find('.js-paypal-account-wrapper').clone();
            $card.prepend(this.$fieldsEl.find('.js-paypal-logo').clone());
            $card.find('.js-paypal-help-text').addClass('hidden');
            $card.find('.js-braintree-delete').addClass('hidden');
            $card.addClass('paypal');
        } else if (this.isPaymentRequestMethod()) {
            $card = this.$fieldsEl.find('.js-payment-request-label').clone();
        }

        // Render selected payment method
        this.$summaryEl.find('.js-summary-card').html($card);

        // Render promotion code
        const promoCode = this.getDataFromContext().promotion_code;
        const promoCodeDisplay = promoCode && promoCode.display;
        this.$summaryEl.find('.js-summary-gift-card').html(promoCodeDisplay || '');
        this.$summaryEl.find('.js-discount-card-trigger').toggleClass('hidden', !!promoCodeDisplay);
    }

    /**
     * Resets the stripe data and card token and Stripe Element form
     */
    resetStripeData() {
        this.hasStripeErrors = false;
        this.stripeData = null;
    }

    /**
     * Sends analytics data related to the discount code / gift card submission
     */
    trackDiscountEvent() {
        const promoCode = this.getDataFromContext().promotion_code;
        const promoCodeValue = this.getPromotionCodeValue()?.value;

        if (promoCode) {
            if (promoCode.errors && promoCode.errors.length) {
                trackDiscountSubmitError(promoCodeValue);
            } else if (promoCode.type_code === 'gift card') {
                trackGiftCardApplied(promoCodeValue);
            } else {
                trackDiscountSubmitSuccess(promoCodeValue);
            }
        }
    }

    /**
     * Updates the displayed errors for the Stripe credit card field
     *
     * @param {Object=} stripeEvent Event returned by Stripe, or null to clear the error message.
     */
    renderStripeErrors(stripeEvent = null) {
        const errors = stripeEvent?.error ? [{ display: stripeEvent.error.message }] : [];
        this.$fieldsEl.find('.js-card-errors .help-block').html(FormFieldComponent.marshalMessageList(errors));
    }

    /**
     * @inheritDoc
     */
    submitErrorAnalytics() {
        trackPaymentSubmitError();
    }

    /**
     * @inheritDoc
     */
    submitSuccessAnalytics() {
        trackPaymentSubmitSuccess(this.getPaymentMethodValue(), false);
    }
}

export default CheckoutPaymentView;
